// Operator (user) profile hook — backed by operator.json.
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { detectHostTimezone } from './timezones';
import { STALE_SSE_COVERED_MS } from './uiConstants';
import type { OperatorResponse } from '@onestone/shared/responses';

export interface OperatorProfile {
  name: string;
  shortName?: string;
  email?: string;
  timezone: string;
  locale?: string;
}

const FALLBACK: OperatorProfile = {
  name: 'Operator',
  timezone: detectHostTimezone(),
};

export function useOperator(): {
  operator: OperatorProfile;
  avatarUrl: string | null;
  displayName: string;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ['operator'],
    queryFn: () => api<OperatorResponse>('/api/operator'),
    staleTime: STALE_SSE_COVERED_MS,
  });
  const operator = query.data?.operator ?? FALLBACK;
  return {
    operator,
    avatarUrl: query.data?.avatarUrl ?? null,
    // Prefer the operator's chosen short name when present — that's the
    // conversational form they want the agent (and UI) to use.
    displayName: (operator.shortName ?? operator.name).trim() || 'Operator',
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
